import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  User,
  Mail,
  Phone,
  Camera,
  Save,
  Edit,
  ArrowLeft,
  Award,
  Copy,
  Check,
  ChevronDown,
  ChevronUp,
  MapPin,
  Globe,
  Clock,
  DollarSign,
  FileText,
  MessageCircle,
  Target,
  Users,
  Calendar,
  TrendingUp,
  Shield,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,
  Building2,
  AlertTriangle,
  Music,
  Instagram as InstagramIcon,
  Facebook as FacebookIcon,
  Send,
  Youtube,
  Plus,
  Headphones,
  Smartphone,
  Mail as MailIcon,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { toast } from "react-toastify";
import AuthenticatedNavbar from "../components/AuthenticatedNavbar";
import authService from "../components/services/AuthService";
import { updateUser } from "../store/slices/authSlice";
import SearchableSelect from "../components/common/SearchableSelect";
import { getAllRegions, getCitiesForRegion } from "../data/ghanaLocations";
import ChannelCard from "../components/auth/ChannelCard";
import ChannelFormDrawer from "../components/auth/ChannelFormDrawer";
const Profile = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [customCity, setCustomCity] = useState("");
  const [availableCities, setAvailableCities] = useState([]);
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('personal');

  // Promotion Channels state
  const [selectedChannel, setSelectedChannel] = useState('whatsapp');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingChannelIndex, setEditingChannelIndex] = useState(null);
  const [channelFormData, setChannelFormData] = useState({
    handle: '',
    link: '',
    audienceSize: '',
    location: '',
    purpose: ''
  });
  const [drawerErrors, setDrawerErrors] = useState({});
  const [isLevelSectionExpanded, setIsLevelSectionExpanded] = useState(false);

  // Get user data from Redux store
  const userData = useSelector((state) => state.auth.user);
  
  // Initialize profile data with both old and new fields
  const [profileData, setProfileData] = useState({
    // Basic Info (always present)
    id: userData?.id || "",
    first_name: userData?.first_name || "",
    last_name: userData?.last_name || "",
    account_display_name: userData?.account_display_name || "",
    affiliate_code: userData?.affiliate_code || "",
    phone_number: userData?.phone_number || "",
    email: userData?.email || "",
    gender: userData?.gender || "",
    profile_image: userData?.profile_image || null,
    account_type: userData?.account_type || "individual",
    
    // Company fields (for company accounts)
    company_website: userData?.company_website || "",
    linkedin_page: userData?.linkedin_page || "",
    
    // Location & Address (new fields - may be null for old users)
    street_address_1: userData?.address?.street_address_1 || "",
    street_address_2: userData?.address?.street_address_2 || "",
    city: userData?.address?.city || userData?.city || "",
    region: userData?.address?.region || userData?.region || "",
    zip_code: userData?.address?.zip_code || userData?.zip_code || "",
    country: userData?.address?.country || userData?.country || "",
    timezone: userData?.timezone || "",
    currency: userData?.currency || "",
    
    // ID Verification (new fields - may be null for old users)
    id_type: userData?.id_type || "",
    id_number: userData?.id_number || "",
    other_id_name: userData?.other_id_name || "",
    
    // Promotion Channels (new structure - may be empty for old users)
    promotion_channels: userData?.promotion_channels || {
      whatsapp: [],
      tiktok: [],
      instagram: [],
      facebook: [],
      telegram: [],
      youtube: [],
      website: [],
      podcast: [],
      mobile_app: [],
      newsletter: [],
      offline: []
    },
    
    // Content & Audience Profile (new fields - may be null for old users)
    content_categories: userData?.content_categories || [],
    target_audiences: userData?.target_audiences || [],
    posting_frequency: userData?.posting_frequency || "",
    weekly_post_count: userData?.weekly_post_count || "",
    
    // Experience & Motivation (new fields - may be null for old users)
    has_affiliate_experience: userData?.has_affiliate_experience ?? null,
    previous_affiliate_platforms: userData?.previous_affiliate_platforms || "",
    motivation_to_join: userData?.motivation_to_join || "",
    
    // Account Status
    is_active: userData?.is_active ?? false,
    affiliate_status: userData?.affiliate_status || null,
    status_reason: userData?.status_reason || null,
  });

  // Channel types definition
  const channelTypes = [
    { 
      id: 'whatsapp', 
      label: 'WhatsApp', 
      icon: MessageCircle, 
      color: 'from-green-500 to-green-600',
      placeholder: 'Status / Groups / Communities'
    },
    { 
      id: 'tiktok', 
      label: 'TikTok', 
      icon: Music, 
      color: 'from-black to-gray-800',
      placeholder: '@username'
    },
    { 
      id: 'instagram', 
      label: 'Instagram', 
      icon: InstagramIcon, 
      color: 'from-pink-500 to-purple-600',
      placeholder: '@username'
    },
    { 
      id: 'facebook', 
      label: 'Facebook', 
      icon: FacebookIcon, 
      color: 'from-blue-600 to-blue-700',
      placeholder: 'Page name or link'
    },
    { 
      id: 'telegram', 
      label: 'Telegram', 
      icon: Send, 
      color: 'from-blue-400 to-blue-500',
      placeholder: 'Channel/Group link'
    },
    { 
      id: 'youtube', 
      label: 'YouTube', 
      icon: Youtube, 
      color: 'from-red-600 to-red-700',
      placeholder: 'Channel link'
    },
    { 
      id: 'website', 
      label: 'Blog/Website', 
      icon: Globe, 
      color: 'from-indigo-500 to-indigo-600',
      placeholder: 'Website URL'
    },
    { 
      id: 'podcast', 
      label: 'Podcast', 
      icon: Headphones, 
      color: 'from-purple-500 to-purple-700',
      placeholder: 'Podcast name or RSS feed'
    },
    { 
      id: 'mobile_app', 
      label: 'Mobile App', 
      icon: Smartphone, 
      color: 'from-cyan-500 to-cyan-600',
      placeholder: 'App name or store link'
    },
    { 
      id: 'newsletter', 
      label: 'Newsletters', 
      icon: MailIcon, 
      color: 'from-blue-500 to-blue-600',
      placeholder: 'Newsletter name or signup link'
    },
    { 
      id: 'offline', 
      label: 'Campus/Offline', 
      icon: Users, 
      color: 'from-orange-500 to-orange-600',
      placeholder: 'Community name'
    }
  ];

  // Update available cities when region changes
  useEffect(() => {
    if (profileData.region) {
      const cities = getCitiesForRegion(profileData.region);
      setAvailableCities(cities);
      
      // If current city is not in the new region's cities and not "__other__", clear it
      if (profileData.city && profileData.city !== "__other__" && !cities.includes(profileData.city)) {
        handleInputChange("city", "");
        setCustomCity("");
      }
    } else {
      setAvailableCities([]);
      if (profileData.city && profileData.city !== "__other__") {
        handleInputChange("city", "");
        setCustomCity("");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData.region]);

  // Initialize custom city if city is "__other__" or not in available cities
  useEffect(() => {
    if (profileData.city && profileData.city !== "__other__" && availableCities.length > 0) {
      if (!availableCities.includes(profileData.city)) {
        // City is not in the list, treat as custom
        setCustomCity(profileData.city);
        handleInputChange("city", "__other__");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData.city, availableCities]);

  // Calculate profile completion - uses same logic as tab completion checks
  const profileCompletion = useMemo(() => {
    // Use the same completion checks as the tabs to ensure consistency
    const personalComplete = profileData.first_name && profileData.last_name && profileData.email && profileData.phone_number && profileData.account_display_name;
    const locationComplete = profileData.city && profileData.region && profileData.country && profileData.timezone && profileData.currency;
    const idVerificationComplete = profileData.id_type && profileData.id_number;
    const promotionChannelsComplete = Object.values(profileData.promotion_channels || {}).some(arr => arr?.length > 0);
    const contentProfileComplete = profileData.content_categories?.length > 0 && 
                                  profileData.target_audiences?.length > 0 && 
                                  profileData.posting_frequency;
    const experienceComplete = profileData.has_affiliate_experience !== null && profileData.motivation_to_join;

    // Calculate weighted completion
    const weights = {
      personal: 20,
      location: 15,
      idVerification: 15,
      promotionChannels: 20,
      contentProfile: 15,
      experience: 15
    };

    let completedWeight = 0;
    if (personalComplete) completedWeight += weights.personal;
    if (locationComplete) completedWeight += weights.location;
    if (idVerificationComplete) completedWeight += weights.idVerification;
    if (promotionChannelsComplete) completedWeight += weights.promotionChannels;
    if (contentProfileComplete) completedWeight += weights.contentProfile;
    if (experienceComplete) completedWeight += weights.experience;

    const totalWeight = Object.values(weights).reduce((sum, weight) => sum + weight, 0);
    const percentage = Math.round((completedWeight / totalWeight) * 100);
    
    return Math.min(100, percentage);
  }, [profileData]);

  // Check if user has new fields (is a new user vs old user)
  const isLegacyUser = useMemo(() => {
    return !profileData.id_type && !profileData.content_categories?.length;
  }, [profileData]);

  // Tab definitions with completion checks
  const tabs = useMemo(() => [
    {
      id: 'personal',
      label: 'Personal Info',
      icon: User,
      isComplete: () => profileData.first_name && profileData.last_name && profileData.email && profileData.phone_number && profileData.account_display_name,
    },
    {
      id: 'location',
      label: 'Location & Address',
      icon: MapPin,
      isComplete: () => profileData.city && profileData.region && profileData.country && profileData.timezone && profileData.currency,
    },
    {
      id: 'idVerification',
      label: 'ID Verification',
      icon: FileText,
      isComplete: () => profileData.id_type && profileData.id_number,
    },
    {
      id: 'promotionChannels',
      label: 'Promotion Channels',
      icon: MessageCircle,
      isComplete: () => Object.values(profileData.promotion_channels || {}).some(arr => arr?.length > 0),
    },
    {
      id: 'contentProfile',
      label: 'Content & Audience',
      icon: Target,
      isComplete: () => profileData.content_categories?.length > 0 && 
                        profileData.target_audiences?.length > 0 && 
                        profileData.posting_frequency,
    },
    {
      id: 'experience',
      label: 'Experience',
      icon: Users,
      isComplete: () => profileData.has_affiliate_experience !== null && profileData.motivation_to_join,
    },
  ], [profileData]);

  // Get current tab data
  const currentTabData = useMemo(() => {
    return tabs.find(tab => tab.id === activeTab);
  }, [tabs, activeTab]);

  const handleInputChange = (field, value) => {
    setProfileData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };


  const handleArrayToggle = (field, value) => {
    setProfileData((prev) => {
      const currentArray = prev[field] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter(item => item !== value)
        : [...currentArray, value];
      return {
        ...prev,
        [field]: newArray
      };
    });
  };

  // Channel management functions
  const getDefaultChannel = () => ({
    handle: '',
    link: '',
    stores: {},
    audienceSize: '',
    location: '',
    purpose: ''
  });

  const normalizePhoneForWhatsApp = (value) => {
    if (!value) return value;
    
    // If it's a URL (wa.me or chat.whatsapp.com), return as-is
    if (value.includes('http') || value.includes('wa.me') || value.includes('chat.whatsapp.com')) {
      return value;
    }
    
    // Remove all non-digits
    let cleanPhone = value.replace(/[^\d]/g, "");
    
    // Handle numbers starting with 0 or 233
    if (cleanPhone.startsWith("233")) {
      // Already has country code, use as-is
      return cleanPhone;
    } else if (cleanPhone.startsWith("0")) {
      // Remove leading 0 and add 233
      return "233" + cleanPhone.substring(1);
    } else if (cleanPhone.length > 0) {
      // Local number without 0, add 233
      return "233" + cleanPhone;
    }
    
    return value;
  };

  const validateChannelLink = (channelType, value) => {
    if (!value) return '';

    const patterns = {
      instagram: /^(@[\w.]+|https?:\/\/(www\.)?instagram\.com\/[\w.]+)/i,
      facebook: /^(https?:\/\/(www\.)?(facebook|fb)\.com\/[\w.-]+|[\w\s.-]+)/i,
      tiktok: /^(@[\w.]+|https?:\/\/(www\.)?tiktok\.com\/@[\w.]+)/i,
      youtube: /^(https?:\/\/(www\.)?(youtube\.com\/(c\/|channel\/|user\/)?|youtu\.be\/)[\w-]+)/i,
      telegram: /^(https?:\/\/(t\.me|telegram\.me)\/[\w]+|@[\w]+)/i,
      whatsapp: /^(\+?\d{10,15}|https?:\/\/(wa\.me|chat\.whatsapp\.com)\/[\w]+)/i,
      website: /^https?:\/\/.+/i,
      podcast: /^([\w\s-]+|https?:\/\/([\w-]+\.)?(spotify|apple|google|anchor|buzzsprout|podbean|stitcher|iheartradio|tunein|castbox|pocketcasts|overcast|castro|downcast|podcastaddict|player\.fm|podchaser|listennotes|podcastindex|rss|feed)\.(com|fm|io|net|org)\/[\w/-]+)/i,
      mobile_app: /^([\w\s-]+|https?:\/\/(play\.google\.com\/store\/apps\/details|apps\.apple\.com\/app|app\.microsoft\.com\/store\/apps\/details|amazon\.com\/gp\/product|f-droid\.org\/packages)\/[\w/.-]+)/i,
      newsletter: /^([\w\s-]+|https?:\/\/.+|mailto:[\w.-]+@[\w.-]+\.\w+)/i
    };

    if (patterns[channelType]) {
      return patterns[channelType].test(value) ? '' : `Please enter a valid ${channelType} handle or link`;
    }

    return '';
  };

  const openAddChannelDrawer = () => {
    if (!isEditing) {
      toast.error("Please enable edit mode first");
      return;
    }
    setChannelFormData(getDefaultChannel());
    setEditingChannelIndex(null);
    setDrawerErrors({});
    setIsDrawerOpen(true);
  };

  const openEditChannelDrawer = (index) => {
    if (!isEditing) {
      toast.error("Please enable edit mode first");
      return;
    }
    const channels = profileData.promotion_channels[selectedChannel] || [];
    const channel = channels[index];
    setChannelFormData({ ...channel });
    setEditingChannelIndex(index);
    setDrawerErrors({});
    setIsDrawerOpen(true);
  };

  const closeChannelDrawer = () => {
    setIsDrawerOpen(false);
    setEditingChannelIndex(null);
    setChannelFormData(getDefaultChannel());
    setDrawerErrors({});
  };

  const deleteChannel = (index) => {
    if (!isEditing) {
      toast.error("Please enable edit mode first");
      return;
    }
    
    setProfileData(prev => ({
      ...prev,
      promotion_channels: {
        ...prev.promotion_channels,
        [selectedChannel]: (prev.promotion_channels[selectedChannel] || []).filter((_, i) => i !== index)
      }
    }));
    
    toast.success("Channel deleted successfully");
  };

  const validateDrawerForm = () => {
    const newErrors = {};

    // For mobile_app, validate stores instead of handle/link
    if (selectedChannel === 'mobile_app') {
      const stores = channelFormData.stores || {};
      const selectedStores = Object.keys(stores).filter(key => stores.hasOwnProperty(key));
      
      if (selectedStores.length === 0) {
        newErrors.stores = 'Please select at least one app store platform';
      } else {
        // Validate each selected store has a link
        const storeErrors = {};
        selectedStores.forEach(storeKey => {
          if (!stores[storeKey]?.trim()) {
            storeErrors[storeKey] = `${storeKey === 'play_store' ? 'Play Store' : storeKey === 'apple_store' ? 'App Store' : 'Huawei Store'} link is required`;
          } else {
            // Validate link format
            const linkError = validateChannelLink(selectedChannel, stores[storeKey]);
            if (linkError) {
              storeErrors[storeKey] = linkError;
            }
          }
        });
        
        if (Object.keys(storeErrors).length > 0) {
          newErrors.stores = storeErrors;
        }
      }
    } else {
      // Validate handle/link for non-mobile_app channels
      if (!channelFormData.handle && !channelFormData.link) {
        newErrors.handle = 'Channel handle or link is required';
      } else {
        const linkError = validateChannelLink(selectedChannel, channelFormData.handle || channelFormData.link);
        if (linkError) {
          newErrors.handle = linkError;
        }
      }
    }

    // Validate audience size
    if (!channelFormData.audienceSize?.trim()) {
      newErrors.audienceSize = 'Audience size is required';
    }

    // Validate location
    if (!channelFormData.location?.trim()) {
      newErrors.location = 'Primary location is required';
    }

    // Validate purpose
    if (!channelFormData.purpose?.trim()) {
      newErrors.purpose = 'Channel purpose is required';
    }

    setDrawerErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveChannel = () => {
    if (!validateDrawerForm()) {
      return;
    }

    // Normalize WhatsApp phone numbers before saving
    let channelToSave = { ...channelFormData };
    if (selectedChannel === 'whatsapp' && (channelFormData.handle || channelFormData.link)) {
      const phoneValue = channelFormData.handle || channelFormData.link;
      const normalized = normalizePhoneForWhatsApp(phoneValue);
      channelToSave = {
        ...channelFormData,
        handle: normalized,
        link: normalized
      };
    }

    if (editingChannelIndex !== null) {
      // Update existing channel
      setProfileData(prev => ({
        ...prev,
        promotion_channels: {
          ...prev.promotion_channels,
          [selectedChannel]: (prev.promotion_channels[selectedChannel] || []).map((channel, index) => 
            index === editingChannelIndex ? channelToSave : channel
          )
        }
      }));
      toast.success("Channel updated successfully");
    } else {
      // Add new channel
      setProfileData(prev => ({
        ...prev,
        promotion_channels: {
          ...prev.promotion_channels,
          [selectedChannel]: [...(prev.promotion_channels[selectedChannel] || []), channelToSave]
        }
      }));
      toast.success("Channel added successfully");
    }

    closeChannelDrawer();
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select a valid image file");
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result;
        setProfileData((prev) => ({
          ...prev,
          profile_image: base64,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      // Prepare comprehensive payload with all fields
      const payload = {
        // Basic Info
        first_name: profileData.first_name,
        last_name: profileData.last_name,
        account_display_name: profileData.account_display_name,
        phone_number: profileData.phone_number,
        email: profileData.email,
        gender: profileData.gender || null,
        profile_image: profileData.profile_image || "",
        
        // Company fields (if applicable)
        ...(profileData.account_type === "company" && {
          company_website: profileData.company_website || null,
          linkedin_page: profileData.linkedin_page || null,
        }),
        
        // Address
        address: {
          street_address_1: profileData.street_address_1 || null,
          street_address_2: profileData.street_address_2 || null,
          city: profileData.city || null,
          region: profileData.region || null,
          zip_code: profileData.zip_code || null,
          country: profileData.country || null,
        },
        
        // Location & Currency
        timezone: profileData.timezone || null,
        currency: profileData.currency || null,
        
        // ID Verification
        id_type: profileData.id_type || null,
        id_number: profileData.id_number || null,
        other_id_name: profileData.id_type === "other" ? profileData.other_id_name : null,
        
        // Promotion Channels
        promotion_channels: profileData.promotion_channels || null,
        
        // Content & Audience
        content_categories: profileData.content_categories || null,
        target_audiences: profileData.target_audiences || null,
        posting_frequency: profileData.posting_frequency || null,
        weekly_post_count: profileData.weekly_post_count || null,
        
        // Experience & Motivation
        has_affiliate_experience: profileData.has_affiliate_experience,
        previous_affiliate_platforms: profileData.previous_affiliate_platforms || null,
        motivation_to_join: profileData.motivation_to_join || null,
      };

      // Call the API
      const response = await authService.updateProfile(payload);
      
      if (response.data.status_code === "000") {
        toast.success(response.data.reason || "Profile updated successfully!");
        dispatch(updateUser(response.data.data));
        setIsEditing(false);
      } else {
        toast.error(response.data.reason || "Failed to update profile. Please try again.");
      }
    } catch (error) {
      console.error("Profile update error:", error);
      toast.error("Failed to update profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    // Reset to original data from Redux
    setProfileData({
      id: userData?.id || "",
      first_name: userData?.first_name || "",
      last_name: userData?.last_name || "",
      account_display_name: userData?.account_display_name || "",
      affiliate_code: userData?.affiliate_code || "",
      phone_number: userData?.phone_number || "",
      email: userData?.email || "",
      gender: userData?.gender || "",
      profile_image: userData?.profile_image || null,
      account_type: userData?.account_type || "individual",
      company_website: userData?.company_website || "",
      linkedin_page: userData?.linkedin_page || "",
      street_address_1: userData?.address?.street_address_1 || "",
      street_address_2: userData?.address?.street_address_2 || "",
      city: userData?.address?.city || userData?.city || "",
      region: userData?.address?.region || userData?.region || "",
      zip_code: userData?.address?.zip_code || userData?.zip_code || "",
      country: userData?.address?.country || userData?.country || "",
      timezone: userData?.timezone || "",
      currency: userData?.currency || "",
      id_type: userData?.id_type || "",
      id_number: userData?.id_number || "",
      other_id_name: userData?.other_id_name || "",
      promotion_channels: userData?.promotion_channels || {
        whatsapp: [],
        tiktok: [],
        instagram: [],
        facebook: [],
        telegram: [],
        youtube: [],
        website: [],
        podcast: [],
        mobile_app: [],
        newsletter: [],
        offline: []
      },
      content_categories: userData?.content_categories || [],
      target_audiences: userData?.target_audiences || [],
      posting_frequency: userData?.posting_frequency || "",
      weekly_post_count: userData?.weekly_post_count || "",
      has_affiliate_experience: userData?.has_affiliate_experience ?? null,
      previous_affiliate_platforms: userData?.previous_affiliate_platforms || "",
      motivation_to_join: userData?.motivation_to_join || "",
      is_active: userData?.is_active ?? false,
      affiliate_status: userData?.affiliate_status || null,
      status_reason: userData?.status_reason || null,
    });
    setCustomCity("");
    setIsEditing(false);
  };

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText(profileData.affiliate_code);
      setIsCopied(true);
      toast.success("Affiliate code copied to clipboard!");
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      toast.error("Failed to copy code");
    }
  };

  // Data structures for rendering
  const contentCategories = [
    { id: 'electronics', label: 'Electronics & Gadgets' },
    { id: 'fashion', label: 'Fashion & Style' },
    { id: 'technology', label: 'Technology' },
    { id: 'lifestyle', label: 'Lifestyle' },
    { id: 'education', label: 'Education' },
    { id: 'hospitality', label: 'Hospitality' },
    { id: 'food', label: 'Food & Beverages' },
    { id: 'health', label: 'Health & Fitness' },
    { id: 'entertainment', label: 'Entertainment' },
    { id: 'travel', label: 'Travel & Tourism' },
    { id: 'sports', label: 'Sports' },
    { id: 'beauty', label: 'Beauty & Cosmetics' },
    { id: 'home', label: 'Home & Living' },
    { id: 'business', label: 'Business & Finance' },
  ];

  const targetAudiences = [
    { id: 'students', label: 'Students' },
    { id: 'professionals', label: 'Working Professionals' },
    { id: 'smes', label: 'SMEs' },
    { id: 'general', label: 'General Public' },
  ];

  const idTypes = [
    { value: "ghana_card", label: "Ghana Card" },
    { value: "passport", label: "Passport" },
    { value: "other", label: "Other Approved ID" },
  ];


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <AuthenticatedNavbar />

      <div className={`px-4 py-16 mx-auto max-w-8xl sm:px-6 lg:px-8 ${isEditing ? 'pb-32' : ''}`}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex gap-4 items-center mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 text-white bg-[#FF7F7F] transition-all duration-300 rounded-xl hover:shadow-md group"
            >
              <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
              <span className="font-semibold">Back</span>
            </button>
          </div>
          <div className="flex justify-between items-center">
            <div className="flex gap-4 items-center">
              <div className="p-3 bg-gradient-to-r from-[#FF7F7F] to-pink-500 rounded-2xl">
                <User className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">My Profile</h1>
                <p className="mt-1 text-gray-600">
                  Manage your account information and preferences
                </p>
              </div>
            </div>
            <div className="flex gap-3 items-center">
              <div className="px-4 py-2 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="flex gap-2 items-center">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="text-xs font-medium text-green-700">Profile Completion</p>
                    <p className="text-lg font-bold text-green-900">{profileCompletion}%</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Affiliate Status Banner - Impressive Display */}
          {profileData.affiliate_status && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4"
            >
              {/* Active and Approved - Can promote and earn */}
              {(profileData.affiliate_status === "full_approval" || profileData.affiliate_status === "limited_approval") && profileData.is_active ? (
                <div className="relative overflow-hidden p-6 bg-gradient-to-r from-emerald-500 via-green-500 to-teal-500 rounded-2xl shadow-xl border-2 border-emerald-400">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
                  <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
                        <CheckCircle2 className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex gap-3 items-center mb-2">
                          <h3 className="text-2xl font-bold text-white">
                            {profileData.affiliate_status === "full_approval" ? "Account Active - Full Access! 🎉" : "Account Active - Limited Access"}
                          </h3>
                          <span className="px-3 py-1 text-xs font-bold text-emerald-700 bg-white rounded-full">
                            ACTIVE
                          </span>
                        </div>
                        <p className="mb-3 text-base text-white/95">
                          {profileData.affiliate_status === "full_approval" 
                            ? "Congratulations! Your affiliate account is active with full access. You can promote all available deals and earn commissions."
                            : "Your affiliate account is active with limited access. You can promote selected campaigns and earn commissions. Contact support for full access."}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <div className="flex gap-2 items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <Award className="w-4 h-4 text-white" />
                            <span className="text-sm font-medium text-white">Ready to Promote</span>
                          </div>
                          <div className="flex gap-2 items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <TrendingUp className="w-4 h-4 text-white" />
                            <span className="text-sm font-medium text-white">Earn Commissions</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (profileData.affiliate_status === "full_approval" || profileData.affiliate_status === "limited_approval") && !profileData.is_active ? (
                <div className="relative overflow-hidden p-6 bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500 rounded-2xl shadow-xl border-2 border-blue-400">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
                  <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
                        <Shield className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex gap-3 items-center mb-2">
                          <h3 className="text-2xl font-bold text-white">
                            {profileData.affiliate_status === "full_approval" ? "Approved - Full Access" : "Approved - Limited Access"}
                          </h3>
                          <span className="px-3 py-1 text-xs font-bold text-blue-700 bg-white rounded-full">
                            APPROVED
                          </span>
                        </div>
                        <p className="mb-3 text-base text-white/95">
                          Great news! Your application has been approved. Your account is currently being activated. 
                          You'll receive an email notification once activation is complete and you can start promoting.
                        </p>
                        <div className="p-4 mb-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                          <div className="flex gap-2 items-start">
                            <Clock className="flex-shrink-0 w-5 h-5 mt-0.5 text-white animate-pulse" />
                            <div>
                              <p className="text-sm font-semibold text-white mb-1">Activation in Progress</p>
                              <p className="text-sm text-white/90">
                                We're completing the final activation steps. This usually takes a few minutes. 
                                You'll be able to access campaigns and start earning once activation is complete.
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <div className="flex gap-2 items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                            <span className="text-sm font-medium text-white">Application Approved</span>
                          </div>
                          <div className="flex gap-2 items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <Clock className="w-4 h-4 text-white" />
                            <span className="text-sm font-medium text-white">Activation Pending</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : profileData.affiliate_status === "rejected" ? (
                <div className="relative overflow-hidden p-6 bg-gradient-to-r from-red-500 via-rose-500 to-pink-500 rounded-2xl shadow-xl border-2 border-red-400">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
                  <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
                        <XCircle className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex gap-3 items-center mb-2">
                          <h3 className="text-2xl font-bold text-white">
                            Application Rejected
                          </h3>
                          <span className="px-3 py-1 text-xs font-bold text-red-700 bg-white rounded-full">
                            REJECTED
                          </span>
                        </div>
                        {profileData.status_reason && (
                          <div className="p-4 mb-3 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                            <div className="flex gap-2 items-start mb-2">
                              <AlertTriangle className="flex-shrink-0 w-5 h-5 mt-0.5 text-white" />
                              <div>
                                <p className="text-sm font-semibold text-white mb-1">Reason:</p>
                                <p className="text-base text-white/95">{profileData.status_reason}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        <p className="mb-3 text-base text-white/95">
                          Your affiliate application was not approved at this time. Please review the reason above 
                          and update your profile accordingly. You can resubmit your application after making the necessary changes.
                        </p>
                        <div className="flex flex-wrap gap-2">
                          <div className="flex gap-2 items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <Edit className="w-4 h-4 text-white" />
                            <span className="text-sm font-medium text-white">Update Profile</span>
                          </div>
                          <div className="flex gap-2 items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <Info className="w-4 h-4 text-white" />
                            <span className="text-sm font-medium text-white">Review Requirements</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : profileData.affiliate_status === "pending" ? (
                <div className="relative overflow-hidden p-6 bg-gradient-to-r from-amber-500 via-orange-500 to-yellow-500 rounded-2xl shadow-xl border-2 border-amber-400">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
                  <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
                        <Clock className="w-8 h-8 text-white animate-pulse" />
                      </div>
                      <div className="flex-1">
                        <div className="flex gap-3 items-center mb-2">
                          <h3 className="text-2xl font-bold text-white">
                            Application Under Review
                          </h3>
                          <span className="px-3 py-1 text-xs font-bold text-amber-700 bg-white rounded-full">
                            PENDING
                          </span>
                        </div>
                        <p className="mb-3 text-base text-white/95">
                          Your affiliate application is currently being reviewed by our team. 
                          We'll notify you via email once a decision has been made.
                        </p>
                        {profileData.status_reason && (
                          <div className="p-3 mb-3 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <p className="text-sm text-white/90">
                              <strong>Note:</strong> {profileData.status_reason}
                            </p>
                          </div>
                        )}
                        <div className="flex flex-wrap gap-2">
                          <div className="flex gap-2 items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <Clock className="w-4 h-4 text-white" />
                            <span className="text-sm font-medium text-white">Review in Progress</span>
                          </div>
                          <div className="flex gap-2 items-center px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                            <Mail className="w-4 h-4 text-white" />
                            <span className="text-sm font-medium text-white">Email Notification</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="relative overflow-hidden p-6 bg-gradient-to-r from-gray-500 via-slate-500 to-zinc-500 rounded-2xl shadow-xl border-2 border-gray-400">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -mr-32 -mt-32"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full -ml-24 -mb-24"></div>
                  <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0 p-4 bg-white/20 backdrop-blur-sm rounded-2xl border border-white/30">
                        <Info className="w-8 h-8 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex gap-3 items-center mb-2">
                          <h3 className="text-2xl font-bold text-white">
                            Status: {profileData.affiliate_status?.charAt(0).toUpperCase() + profileData.affiliate_status?.slice(1)}
                          </h3>
                        </div>
                        {profileData.status_reason && (
                          <p className="mb-3 text-base text-white/95">
                            {profileData.status_reason}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* Publisher Level Information - Show for approved/active users */}
          {(profileData.affiliate_status === "full_approval" || profileData.affiliate_status === "limited_approval") && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4"
            >
              <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 rounded-2xl border-2 border-purple-200 shadow-lg overflow-hidden">
                {/* Collapsible Header */}
                <button
                  onClick={() => setIsLevelSectionExpanded(!isLevelSectionExpanded)}
                  className="w-full p-6 flex items-center justify-between hover:bg-purple-100/50 transition-colors"
                >
                  <div className="flex gap-4 items-center">
                    <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
                      <Shield className="w-8 h-8 text-white" />
                    </div>
                    <div className="text-left">
                      <div className="flex gap-2 items-center mb-1">
                        <h3 className="text-xl font-bold text-gray-900">Publisher Level</h3>
                        <span className="px-2 py-0.5 text-xs font-bold text-purple-700 bg-white rounded-full">
                          LEVEL 1
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">Basic Publisher - Entry level for all new publishers</p>
                    </div>
                  </div>
                  {isLevelSectionExpanded ? (
                    <ChevronUp className="w-6 h-6 text-gray-600" />
                  ) : (
                    <ChevronDown className="w-6 h-6 text-gray-600" />
                  )}
                </button>

                {/* Collapsible Content */}
                <AnimatePresence>
                  {isLevelSectionExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 space-y-4 border-t border-purple-200">
                        <div className="pt-4">
                          <p className="text-sm text-gray-700">
                            All new publishers start at <strong>Level 1 - Basic Publisher</strong>. This is your entry point into the affiliate program.
                          </p>
                        </div>

                        {/* Why Level 1 */}
                        <div className="p-4 bg-white/60 rounded-xl border border-purple-200">
                          <div className="flex gap-3 items-start mb-3">
                            <Info className="flex-shrink-0 w-5 h-5 mt-0.5 text-purple-600" />
                            <div>
                              <h4 className="mb-1 text-sm font-semibold text-gray-900">Why You're at Level 1</h4>
                              <p className="text-sm text-gray-700">
                                This is the default starting level for all new publishers. It's designed to protect brands and help you learn the platform while you establish your performance history.
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Current Benefits */}
                        <div className="p-4 bg-white/60 rounded-xl border border-purple-200">
                          <h4 className="mb-3 text-sm font-semibold text-gray-900">Your Current Benefits</h4>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div className="flex gap-2 items-center">
                              <CheckCircle2 className="flex-shrink-0 w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-700">Access to general campaigns</span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <CheckCircle2 className="flex-shrink-0 w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-700">Standard commission rates</span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <CheckCircle2 className="flex-shrink-0 w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-700">Basic campaign analytics</span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <CheckCircle2 className="flex-shrink-0 w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-700">Email support</span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <CheckCircle2 className="flex-shrink-0 w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-700">Platform onboarding support</span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <CheckCircle2 className="flex-shrink-0 w-4 h-4 text-green-600" />
                              <span className="text-sm text-gray-700">Access to training resources</span>
                            </div>
                          </div>
                        </div>

                        {/* How to Progress */}
                        <div className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border-2 border-indigo-200">
                          <div className="flex gap-3 items-start mb-3">
                            <TrendingUp className="flex-shrink-0 w-5 h-5 mt-0.5 text-indigo-600" />
                            <div className="flex-1">
                              <h4 className="mb-2 text-sm font-semibold text-gray-900">How to Progress to Level 2</h4>
                              <p className="mb-3 text-sm text-gray-700">
                                To upgrade to <strong>Level 2 - Established Publisher</strong>, you need to:
                              </p>
                              <ul className="space-y-2 text-sm text-gray-700">
                                <li className="flex gap-2 items-start">
                                  <Target className="flex-shrink-0 w-4 h-4 mt-0.5 text-indigo-600" />
                                  <span><strong>Achieve a performance score of 60 or higher</strong> - Based on engagement quality, conversion performance, compliance, and consistency</span>
                                </li>
                                <li className="flex gap-2 items-start">
                                  <Calendar className="flex-shrink-0 w-4 h-4 mt-0.5 text-indigo-600" />
                                  <span><strong>Be active for 3+ months</strong> - Consistently promote campaigns and maintain activity</span>
                                </li>
                                <li className="flex gap-2 items-start">
                                  <Shield className="flex-shrink-0 w-4 h-4 mt-0.5 text-indigo-600" />
                                  <span><strong>Maintain a positive compliance record</strong> - Follow platform rules and guidelines</span>
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>

                        {/* Level 2 Benefits Preview */}
                        <div className="p-4 bg-white/60 rounded-xl border border-indigo-200">
                          <h4 className="mb-2 text-sm font-semibold text-gray-900">Level 2 Benefits (Coming Soon)</h4>
                          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                            <div className="flex gap-2 items-center">
                              <Award className="flex-shrink-0 w-4 h-4 text-indigo-600" />
                              <span className="text-sm text-gray-700">Access to premium campaigns</span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <Award className="flex-shrink-0 w-4 h-4 text-indigo-600" />
                              <span className="text-sm text-gray-700">Enhanced commission rates</span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <Award className="flex-shrink-0 w-4 h-4 text-indigo-600" />
                              <span className="text-sm text-gray-700">Advanced analytics</span>
                            </div>
                            <div className="flex gap-2 items-center">
                              <Award className="flex-shrink-0 w-4 h-4 text-indigo-600" />
                              <span className="text-sm text-gray-700">Dedicated account manager</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}
          
          {/* Account Activation Status Banner */}
          {!profileData.is_active && !profileData.affiliate_status && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 mt-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border-2 border-amber-300 shadow-md"
            >
              <div className="flex gap-3 items-start">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Shield className="flex-shrink-0 w-6 h-6 text-amber-600" />
                </div>
                <div className="flex-1">
                  <h3 className="mb-2 text-base font-bold text-amber-900">
                    Account Pending Activation
                  </h3>
                  <p className="mb-3 text-sm text-amber-800">
                    Your account is currently under review and not yet active for affiliate advertisement. 
                    Our team is verifying your information and promotion channels.
                  </p>
                  <div className="space-y-1.5">
                    <div className="flex gap-2 items-start">
                      <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5 text-amber-700" />
                      <p className="text-xs text-amber-700">
                        <strong>What's Next:</strong> Complete all profile sections to speed up the review process
                      </p>
                    </div>
                    <div className="flex gap-2 items-start">
                      <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5 text-amber-700" />
                      <p className="text-xs text-amber-700">
                        <strong>Review Time:</strong> Typically 24-48 hours after profile completion
                      </p>
                    </div>
                    <div className="flex gap-2 items-start">
                      <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5 text-amber-700" />
                      <p className="text-xs text-amber-700">
                        <strong>Notification:</strong> You'll receive an email once your account is activated
                      </p>
                    </div>
                  </div>
                  {profileCompletion < 100 && (
                    <div className="p-3 mt-3 rounded-lg border border-amber-200 bg-white/60">
                      <p className="text-xs font-medium text-amber-900">
                        ⚠️ Your profile is {profileCompletion}% complete. Complete all sections to begin the review process.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Legacy User Migration Banner */}
          {isLegacyUser && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 mt-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200"
            >
              <div className="flex gap-3 items-start">
                <Info className="flex-shrink-0 w-5 h-5 mt-0.5 text-blue-600" />
                <div>
                  <h3 className="mb-1 text-sm font-semibold text-blue-900">
                    Complete Your Profile
                  </h3>
                  <p className="text-xs text-blue-700">
                    We've added new features! Please update your profile with promotion channels, 
                    content preferences, and ID verification to unlock all platform benefits.
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Global Edit/Save Controls */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex justify-between items-center p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div>
              <h3 className="text-sm font-medium text-gray-700">
                {isEditing ? "Editing Mode" : "View Mode"}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {isEditing ? "Make your changes and save below" : "Click Edit Profile to make changes"}
              </p>
            </div>
            <div className="flex gap-3">
              {isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isLoading}
                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#FF7F7F] to-pink-500 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 font-medium"
                  >
                    <Save className="w-4 h-4" />
                    {isLoading ? "Saving..." : "Save All Changes"}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => {
                    setIsEditing(true);
                    // Ensure Ghana values are set when entering edit mode
                    setProfileData((prev) => ({
                      ...prev,
                      country: prev.country || "ghana",
                      timezone: prev.timezone || "africa/accra",
                      currency: prev.currency || "ghs",
                    }));
                  }}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#FF7F7F] to-pink-500 text-white rounded-lg hover:shadow-lg transition-all font-medium"
                >
                  <Edit className="w-4 h-4" />
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </motion.div>

        {/* Profile Header Banner - Full Width */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="overflow-hidden relative mb-6 bg-white rounded-2xl border border-gray-100 shadow-lg"
        >
          {/* Gradient Background */}
          <div className="absolute top-0 left-0 right-0 h-full bg-gradient-to-r from-[#FF7F7F] via-pink-500 to-purple-500"></div>
          
          <div className="relative p-8">
            <div className="flex flex-col gap-6 justify-between items-center lg:flex-row">
              {/* Avatar & Name */}
              <div className="flex flex-col gap-6 items-center lg:flex-row">
                <div className="relative">
                  {profileData.profile_image ? (
                    <img
                      src={profileData.profile_image}
                      alt="Profile"
                      className="object-cover w-32 h-32 rounded-full border-4 border-white shadow-2xl"
                    />
                  ) : (
                    <div className="flex justify-center items-center w-32 h-32 text-5xl font-bold text-white bg-gradient-to-r rounded-full border-4 border-white shadow-2xl backdrop-blur-sm from-white/20 to-white/30">
                      {profileData.account_display_name?.charAt(0) || profileData.first_name?.charAt(0) || "U"}
                    </div>
                  )}
                  {isEditing && (
                    <>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="hidden"
                        id="profile-image-upload"
                      />
                      <label
                        htmlFor="profile-image-upload"
                        className="absolute bottom-0 right-0 p-3 bg-white text-[#FF7F7F] rounded-full hover:bg-gray-100 transition-all cursor-pointer shadow-lg hover:scale-110"
                      >
                        <Camera className="w-5 h-5" />
                      </label>
                    </>
                  )}
                </div>
                
                <div className="text-center lg:text-left">
                  <h2 className="mb-2 text-3xl font-bold text-white">
                    {profileData.account_display_name || `${profileData.first_name} ${profileData.last_name}`}
                  </h2>
                  <div className="flex flex-col gap-3 items-center lg:flex-row">
                    <div className="flex gap-2 items-center">
                      <Mail className="w-4 h-4 text-white/80" />
                      <p className="text-white/90">{profileData.email}</p>
                    </div>
                    <div className="hidden w-1 h-1 rounded-full lg:block bg-white/50"></div>
                    <div className="flex gap-2 items-center">
                      <Phone className="w-4 h-4 text-white/80" />
                      <p className="text-white/90">{profileData.phone_number}</p>
                    </div>
                    <div className="hidden w-1 h-1 rounded-full lg:block bg-white/50"></div>
                    <div className="px-3 py-1 rounded-full backdrop-blur-sm bg-white/20">
                      <p className="text-xs font-semibold text-white capitalize">
                        {profileData.account_type} Account
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Affiliate Code */}
              <div className="p-5 rounded-xl border backdrop-blur-md bg-white/10 border-white/20">
                <div className="flex gap-2 items-center mb-2">
                  <Award className="w-5 h-5 text-[#1dff37]" />
                  <p className="text-sm font-medium text-white">Your Affiliate Code</p>
                </div>
                <div className="flex gap-3 items-center">
                  <p className="text-2xl font-bold tracking-wider text-white">{profileData.affiliate_code}</p>
                  <button
                    onClick={handleCopyCode}
                    className="p-2 rounded-lg transition-all duration-300 bg-white/20 hover:bg-white/30 hover:scale-110 active:scale-95"
                    title="Copy to clipboard"
                  >
                    {isCopied ? (
                      <Check className="w-5 h-5 text-[#1dff37]" />
                    ) : (
                      <Copy className="w-5 h-5 text-white" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
          {/* Left Sidebar - Tab Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-6 space-y-2">
              {/* Tab Navigation */}
              {tabs.map((tab, index) => {
                const IconComponent = tab.icon;
                const isActive = activeTab === tab.id;
                const isComplete = tab.isComplete();
                
                return (
                  <motion.button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                      isActive
                        ? 'border-[#FF7F7F] bg-gradient-to-r from-red-50 to-pink-50 shadow-md'
                        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div className="flex gap-3 items-center">
                        <div className={`p-2 rounded-lg ${
                          isActive ? 'bg-[#FF7F7F]/10' : 'bg-gray-100'
                        }`}>
                          <IconComponent className={`w-5 h-5 ${
                            isActive ? 'text-[#FF7F7F]' : 'text-gray-600'
                          }`} />
                        </div>
                        <div>
                          <p className={`text-sm font-semibold ${
                            isActive ? 'text-[#FF7F7F]' : 'text-gray-900'
                          }`}>
                            {tab.label}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Step {index + 1}
                          </p>
                        </div>
                      </div>
                      {isComplete ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                      )}
                    </div>
                  </motion.button>
                );
              })}

              {/* Progress Summary */}
              <div className="p-4 mt-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl border border-green-200">
                <div className="flex gap-2 items-center mb-2">
                  <TrendingUp className="w-4 h-4 text-green-600" />
                  <p className="text-xs font-semibold text-green-900">Profile Progress</p>
                </div>
                <div className="mb-2">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-2xl font-bold text-green-900">{profileCompletion}%</span>
                    <span className="text-xs text-green-700">
                      {tabs.filter(tab => tab.isComplete()).length}/{tabs.length} Complete
                    </span>
                  </div>
                  <div className="overflow-hidden w-full h-2 bg-green-200 rounded-full">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${profileCompletion}%` }}
                      transition={{ duration: 0.5 }}
                      className="h-full bg-gradient-to-r from-green-500 to-emerald-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-green-700">
                  {profileCompletion === 100 
                    ? '🎉 Your profile is complete!' 
                    : 'Complete your profile to unlock all features'}
                </p>
              </div>
            </div>
          </div>

          {/* Right Content Area - Tab Content */}
          <div className="lg:col-span-3">
            {/* Tab Content Container */}
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
                className="p-8 bg-white rounded-2xl border border-gray-200 shadow-sm"
              >
                {/* Tab Header */}
                <div className="pb-6 mb-6 border-b border-gray-200">
                  <div className="flex gap-3 items-center">
                    {currentTabData && (
                      <>
                        <div className="p-3 bg-gradient-to-r from-[#FF7F7F] to-pink-500 rounded-xl">
                          <currentTabData.icon className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h2 className="text-2xl font-bold text-gray-900">{currentTabData.label}</h2>
                          <p className="mt-1 text-sm text-gray-600">
                            {currentTabData.isComplete() ? (
                              <span className="flex gap-1 items-center text-green-600">
                                <CheckCircle2 className="w-4 h-4" />
                                This section is complete
                              </span>
                            ) : (
                              <span className="flex gap-1 items-center text-amber-600">
                                <AlertCircle className="w-4 h-4" />
                                Please complete this section
                              </span>
                            )}
                          </p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Personal Information Tab */}
                {activeTab === 'personal' && (
                  <div className="space-y-4">
                {/* Form Fields */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div>
                      <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                        <User className="w-4 h-4 text-gray-400" />
                        First Name
                      </label>
                      <input
                        type="text"
                        value={profileData.first_name}
                        onChange={(e) =>
                          handleInputChange("first_name", e.target.value)
                        }
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      />
                    </div>
                    
                    <div>
                      <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                        <User className="w-4 h-4 text-gray-400" />
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={profileData.last_name}
                        onChange={(e) =>
                          handleInputChange("last_name", e.target.value)
                        }
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                        <Award className="w-4 h-4 text-gray-400" />
                        Display Name
                      </label>
                      <input
                        type="text"
                        value={profileData.account_display_name}
                        onChange={(e) =>
                          handleInputChange("account_display_name", e.target.value)
                        }
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      />
                    </div>
                    
                    <div>
                      <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                        <Mail className="w-4 h-4 text-gray-400" />
                        Email Address
                      </label>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) =>
                          handleInputChange("email", e.target.value)
                        }
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      />
                    </div>
                    
                    <div>
                      <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                        <Phone className="w-4 h-4 text-gray-400" />
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        value={profileData.phone_number}
                        onChange={(e) =>
                          handleInputChange("phone_number", e.target.value)
                        }
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      />
                    </div>
                    
                    <div className="md:col-span-2">
                      <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                        <User className="w-4 h-4 text-gray-400" />
                        Gender
                      </label>
                      <select
                        value={profileData.gender || ""}
                        onChange={(e) =>
                          handleInputChange("gender", e.target.value)
                        }
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      >
                        <option value="">Prefer not to say</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Company Fields - Only show for company accounts */}
                  {profileData.account_type === "company" && (
                    <>
                      <div className="pt-4 border-t border-gray-200 md:col-span-2">
                        <h3 className="mb-4 text-sm font-semibold text-gray-700">Company Information</h3>
                      </div>
                      <div className="md:col-span-2">
                        <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                          <Globe className="w-4 h-4 text-gray-400" />
                          Company Website <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="url"
                          value={profileData.company_website}
                          onChange={(e) => handleInputChange("company_website", e.target.value)}
                          disabled={!isEditing}
                          placeholder="https://yourcompany.com"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                          <Globe className="w-4 h-4 text-gray-400" />
                          LinkedIn Company Page <span className="text-red-600">*</span>
                        </label>
                        <input
                          type="url"
                          value={profileData.linkedin_page}
                          onChange={(e) => handleInputChange("linkedin_page", e.target.value)}
                          disabled={!isEditing}
                          placeholder="https://linkedin.com/company/yourcompany"
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                        />
                      </div>
                    </>
                  )}
                  </div>
                )}

                {/* Location & Address Tab */}
                {activeTab === 'location' && (
                  <div className="space-y-4">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {/* Street Address */}
                  <div className="md:col-span-2">
                    <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      Street Address
                    </label>
                    <input
                      type="text"
                      value={profileData.street_address_1}
                      onChange={(e) => handleInputChange("street_address_1", e.target.value)}
                      disabled={!isEditing}
                      placeholder="Street address line 1"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <input
                      type="text"
                      value={profileData.street_address_2}
                      onChange={(e) => handleInputChange("street_address_2", e.target.value)}
                      disabled={!isEditing}
                      placeholder="Street address line 2 (optional)"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                    />
                  </div>

                  {/* Region Selection */}
                  <div>
                    <SearchableSelect
                      value={profileData.region || ""}
                      onChange={(value) => handleInputChange("region", value)}
                      options={getAllRegions()}
                      placeholder="Select Region"
                      label={
                        <span className="flex items-center space-x-1">
                          <span>Region</span>
                        </span>
                      }
                      icon={Building2}
                      disabled={!isEditing}
                      showOtherOption={false}
                    />
                  </div>

                  {/* City / Town Selection */}
                  <div>
                    <SearchableSelect
                      value={profileData.city === "__other__" ? "__other__" : profileData.city || ""}
                      onChange={(value) => {
                        if (value === "__other__") {
                          handleInputChange("city", "__other__");
                          setCustomCity("");
                        } else {
                          handleInputChange("city", value);
                          setCustomCity("");
                        }
                      }}
                      options={availableCities}
                      placeholder={profileData.region ? "Select City / Town" : "Select region first"}
                      disabled={!isEditing || !profileData.region}
                      label={
                        <span className="flex items-center space-x-1">
                          <span>City / Town</span>
                          <span className="text-red-600">*</span>
                        </span>
                      }
                      icon={MapPin}
                      showOtherOption={true}
                      otherOptionLabel="Other (Custom Location)"
                      emptyMessage="No cities found. Select 'Other' to enter custom location."
                    />

                    {/* Custom City Input */}
                    <AnimatePresence>
                      {profileData.city === "__other__" && isEditing && (
                        <motion.div
                          initial={{ opacity: 0, height: 0, marginTop: 0 }}
                          animate={{ opacity: 1, height: "auto", marginTop: 8 }}
                          exit={{ opacity: 0, height: 0, marginTop: 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 flex-shrink-0 w-5 h-5 text-orange-500 -translate-y-1/2 pointer-events-none" />
                            <input
                              type="text"
                              value={customCity}
                              onChange={(e) => {
                                const value = e.target.value;
                                setCustomCity(value);
                                // Update the actual city field with custom value
                                handleInputChange("city", value);
                              }}
                              placeholder="Enter your custom location"
                              className="py-3 pr-4 pl-10 w-full placeholder-orange-400 text-gray-900 bg-orange-50 rounded-lg border-2 border-orange-300 transition-all duration-200 focus:ring-2 focus:bg-white focus:ring-orange-500 focus:border-transparent"
                              autoFocus
                            />
                          </div>
                          <p className="flex items-center mt-1 space-x-1 text-xs text-orange-600">
                            <span>✏️</span>
                            <span>Enter your custom city or town name</span>
                          </p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Zip Code */}
                  <div>
                    <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      Zip / Postal Code
                    </label>
                    <input
                      type="text"
                      value={profileData.zip_code}
                      onChange={(e) => handleInputChange("zip_code", e.target.value)}
                      disabled={!isEditing}
                      placeholder="Zip code"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                    />
                  </div>

                  {/* Country - Locked to Ghana */}
                  <div>
                    <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                      <Globe className="w-4 h-4 text-gray-400" />
                      Country <span className="text-red-600">*</span>
                    </label>
                    <div className="px-4 py-2.5 w-full text-gray-700 bg-gray-100 rounded-lg border border-gray-300 cursor-not-allowed">
                      🇬🇭 Ghana
                    </div>
                    {isEditing && (
                      <input type="hidden" value="ghana" onChange={() => handleInputChange("country", "ghana")} />
                    )}
                  </div>

                  {/* Timezone - Locked to Africa/Accra */}
                  <div>
                    <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                      <Clock className="w-4 h-4 text-gray-400" />
                      Timezone <span className="text-red-600">*</span>
                    </label>
                    <div className="px-4 py-2.5 w-full text-gray-700 bg-gray-100 rounded-lg border border-gray-300 cursor-not-allowed">
                      Africa/Accra (GMT+0)
                    </div>
                    {isEditing && (
                      <input type="hidden" value="africa/accra" onChange={() => handleInputChange("timezone", "africa/accra")} />
                    )}
                  </div>

                  {/* Currency - Locked to GHS */}
                  <div>
                    <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                      <DollarSign className="w-4 h-4 text-gray-400" />
                      Currency <span className="text-red-600">*</span>
                    </label>
                    <div className="px-4 py-2.5 w-full text-gray-700 bg-gray-100 rounded-lg border border-gray-300 cursor-not-allowed">
                      Ghana Cedi (GHS) ₵
                    </div>
                    {isEditing && (
                      <input type="hidden" value="ghs" onChange={() => handleInputChange("currency", "ghs")} />
                    )}
                  </div>
                </div>
                  </div>
                )}

                {/* ID Verification Tab */}
                {activeTab === 'idVerification' && (
                  <div className="space-y-4">
                {isLegacyUser && !profileData.id_type && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-xs text-amber-700">
                      <strong>Note:</strong> ID verification is required before your first payout. Please add your ID details.
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                      <FileText className="w-4 h-4 text-gray-400" />
                      ID Type <span className="text-red-600">*</span>
                    </label>
                    <select
                      value={profileData.id_type}
                      onChange={(e) => handleInputChange("id_type", e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                    >
                      <option value="">Select ID Type</option>
                      {idTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  {profileData.id_type === "other" && (
                    <div>
                      <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                        <FileText className="w-4 h-4 text-gray-400" />
                        Specify ID Type <span className="text-red-600">*</span>
                      </label>
                      <input
                        type="text"
                        value={profileData.other_id_name}
                        onChange={(e) => handleInputChange("other_id_name", e.target.value)}
                        disabled={!isEditing}
                        placeholder="e.g., National ID, Driver's License"
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      />
                    </div>
                  )}

                  <div className={profileData.id_type === "other" ? "md:col-span-2" : ""}>
                    <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                      <FileText className="w-4 h-4 text-gray-400" />
                      ID Number <span className="text-red-600">*</span>
                    </label>
                    <input
                      type="text"
                      value={profileData.id_number}
                      onChange={(e) => handleInputChange("id_number", e.target.value)}
                      disabled={!isEditing}
                      placeholder="Enter your ID number"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                    />
                  </div>
                </div>
                  </div>
                )}

                {/* Promotion Channels Tab */}
                {activeTab === 'promotionChannels' && (
                  <div className="space-y-6">
                    {isLegacyUser && !Object.values(profileData.promotion_channels || {}).some(arr => arr?.length > 0) && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-xs text-blue-700">
                          <strong>New Feature:</strong> Add your promotion channels to help us match you with relevant campaigns.
                          This section was added after your registration.
                        </p>
                      </div>
                    )}

                    {/* UX Guidelines Section */}
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200"
                    >
                      <div className="flex items-start space-x-3">
                        <Info className="flex-shrink-0 w-5 h-5 mt-0.5 text-blue-600" />
                        <div className="flex-1">
                          <h3 className="mb-2 text-sm font-semibold text-blue-900">
                            Channel Verification Guidelines:
                          </h3>
                          <div className="space-y-2 text-xs text-blue-800">
                            <div className="flex items-start space-x-2">
                              <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5" />
                              <span>Provide accurate and clickable links for all social media channels</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5" />
                              <span>Ensure handles are spelled correctly for verification purposes</span>
                            </div>
                            <div className="flex items-start space-x-2">
                              <CheckCircle2 className="flex-shrink-0 w-4 h-4 mt-0.5" />
                              <span>All declared channels will be reviewed during the vetting process</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* Important Note */}
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="p-4 bg-amber-50 rounded-lg border border-amber-200"
                    >
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="flex-shrink-0 w-5 h-5 mt-0.5 text-amber-600" />
                        <div>
                          <p className="text-sm font-medium text-amber-900">
                            Important Note
                          </p>
                          <p className="mt-1 text-xs text-amber-700">
                            Follower count alone does not determine approval. We value authentic engagement, 
                            content quality, and audience relevance over pure numbers.
                          </p>
                        </div>
                      </div>
                    </motion.div>

                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                      {/* Left Column - Channel Selection */}
                      <div className="lg:col-span-1">
                        <h3 className="mb-3 text-sm font-semibold text-gray-800">Select Promotion Channel</h3>
                        <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-1">
                          {channelTypes.map((channel) => {
                            const IconComponent = channel.icon;
                            const isSelected = selectedChannel === channel.id;
                            const channelCount = profileData.promotion_channels?.[channel.id]?.length || 0;
                            
                            return (
                              <motion.button
                                key={channel.id}
                                onClick={() => setSelectedChannel(channel.id)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`w-full p-3 rounded-lg border-2 transition-all duration-200 ${
                                  isSelected 
                                    ? `border-transparent bg-gradient-to-r ${channel.color} text-white shadow-lg` 
                                    : 'border-gray-300 bg-white hover:border-gray-400 text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center space-x-3">
                                    <IconComponent className="w-5 h-5" />
                                    <span className="text-sm font-medium">{channel.label}</span>
                                  </div>
                                  {channelCount > 0 && (
                                    <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                                      isSelected ? 'bg-white/20' : 'bg-gray-200 text-gray-700'
                                    }`}>
                                      {channelCount}
                                    </span>
                                  )}
                                </div>
                              </motion.button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Right Column - Channel Details */}
                      <div className="lg:col-span-2">
                        <div className="p-6 bg-gray-50 rounded-lg border border-gray-200">
                          <div className="flex justify-between items-center mb-6">
                            <div>
                              <h3 className="text-lg font-semibold text-gray-800">
                                {channelTypes.find(c => c.id === selectedChannel)?.label}
                              </h3>
                              <p className="mt-1 text-xs text-gray-500">
                                {channelTypes.find(c => c.id === selectedChannel)?.placeholder}
                              </p>
                            </div>
                            <motion.button
                              onClick={openAddChannelDrawer}
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              disabled={!isEditing}
                              className="flex items-center px-4 py-2 space-x-2 text-sm text-white bg-gradient-to-r from-red-500 to-red-600 rounded-lg shadow-lg transition-all duration-200 hover:from-red-600 hover:to-red-700 shadow-red-500/25 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Plus className="w-4 h-4" />
                              <span>Add {channelTypes.find(c => c.id === selectedChannel)?.label}</span>
                            </motion.button>
                          </div>

                          <AnimatePresence mode="wait">
                            {(profileData.promotion_channels?.[selectedChannel]?.length || 0) === 0 ? (
                              <motion.div
                                key="empty"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="py-12 text-center text-gray-500"
                              >
                                {(() => {
                                  const selectedChannelData = channelTypes.find(c => c.id === selectedChannel);
                                  return selectedChannelData && <selectedChannelData.icon className="mx-auto mb-3 w-16 h-16 text-gray-300" />;
                                })()}
                                <p className="text-sm">No {channelTypes.find(c => c.id === selectedChannel)?.label} channels added yet</p>
                                <p className="mt-1 text-xs text-gray-400">
                                  {isEditing 
                                    ? `Click "Add ${channelTypes.find(c => c.id === selectedChannel)?.label}" to get started`
                                    : 'Enable edit mode to add channels'}
                                </p>
                              </motion.div>
                            ) : (
                              <motion.div
                                key="channels"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="space-y-3"
                              >
                                {(profileData.promotion_channels[selectedChannel] || []).map((channel, index) => (
                                  <ChannelCard
                                    key={index}
                                    channel={channel}
                                    channelType={selectedChannel}
                                    index={index}
                                    onEdit={openEditChannelDrawer}
                                    onDelete={deleteChannel}
                                    icon={channelTypes.find(c => c.id === selectedChannel)?.icon}
                                  />
                                ))}
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Content & Audience Profile Tab */}
                {activeTab === 'contentProfile' && (
                  <div className="space-y-6">
                {isLegacyUser && !profileData.content_categories?.length && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700">
                      <strong>New Feature:</strong> Help us match you with better campaigns by sharing your content preferences.
                    </p>
                  </div>
                )}

                {/* Content Categories */}
                <div>
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    Content Categories {!isEditing && profileData.content_categories?.length > 0 && (
                      <span className="text-xs text-gray-500">({profileData.content_categories.length} selected)</span>
                    )}
                  </label>
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                      {contentCategories.map(category => (
                        <button
                          key={category.id}
                          type="button"
                          onClick={() => handleArrayToggle('content_categories', category.id)}
                          className={`p-2 text-xs rounded-lg border-2 transition-all ${
                            profileData.content_categories?.includes(category.id)
                              ? 'border-[#FF7F7F] bg-red-50 text-[#FF7F7F] font-medium'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          {category.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {profileData.content_categories?.length > 0 ? (
                        profileData.content_categories.map(catId => {
                          const category = contentCategories.find(c => c.id === catId);
                          return category ? (
                            <span key={catId} className="px-3 py-1 text-xs font-medium text-[#FF7F7F] bg-red-50 rounded-full border border-red-200">
                              {category.label}
                            </span>
                          ) : null;
                        })
                      ) : (
                        <span className="text-sm text-gray-500">Not set</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Target Audiences */}
                <div>
                  <label className="block mb-3 text-sm font-medium text-gray-700">
                    Target Audiences {!isEditing && profileData.target_audiences?.length > 0 && (
                      <span className="text-xs text-gray-500">({profileData.target_audiences.length} selected)</span>
                    )}
                  </label>
                  {isEditing ? (
                    <div className="grid grid-cols-2 gap-2">
                      {targetAudiences.map(audience => (
                        <button
                          key={audience.id}
                          type="button"
                          onClick={() => handleArrayToggle('target_audiences', audience.id)}
                          className={`p-3 text-sm rounded-lg border-2 transition-all ${
                            profileData.target_audiences?.includes(audience.id)
                              ? 'border-[#FF7F7F] bg-red-50 text-[#FF7F7F] font-medium'
                              : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                          }`}
                        >
                          {audience.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {profileData.target_audiences?.length > 0 ? (
                        profileData.target_audiences.map(audId => {
                          const audience = targetAudiences.find(a => a.id === audId);
                          return audience ? (
                            <span key={audId} className="px-3 py-1 text-xs font-medium text-[#FF7F7F] bg-red-50 rounded-full border border-red-200">
                              {audience.label}
                            </span>
                          ) : null;
                        })
                      ) : (
                        <span className="text-sm text-gray-500">Not set</span>
                      )}
                    </div>
                  )}
                </div>

                {/* Posting Frequency */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      Posting Frequency
                    </label>
                    <select
                      value={profileData.posting_frequency}
                      onChange={(e) => handleInputChange("posting_frequency", e.target.value)}
                      disabled={!isEditing}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all capitalize"
                    >
                      <option value="">Select frequency</option>
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="occasional">Occasional</option>
                    </select>
                  </div>

                  {profileData.posting_frequency === 'weekly' && (
                    <div>
                      <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                        <Calendar className="w-4 h-4 text-gray-400" />
                        Posts Per Week
                      </label>
                      <select
                        value={profileData.weekly_post_count}
                        onChange={(e) => handleInputChange("weekly_post_count", e.target.value)}
                        disabled={!isEditing}
                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                      >
                        <option value="">Select count</option>
                        <option value="1-2">1-2 times per week</option>
                        <option value="3-4">3-4 times per week</option>
                        <option value="5-6">5-6 times per week</option>
                        <option value="7+">7+ times per week</option>
                      </select>
                    </div>
                  )}
                </div>
                  </div>
                )}

                {/* Experience & Motivation Tab */}
                {activeTab === 'experience' && (
                  <div className="space-y-4">
                {isLegacyUser && profileData.has_affiliate_experience === null && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-xs text-blue-700">
                      <strong>New Feature:</strong> Share your experience to help us provide better support.
                    </p>
                  </div>
                )}

                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Affiliate Marketing Experience
                  </label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => isEditing && handleInputChange("has_affiliate_experience", true)}
                      disabled={!isEditing}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        profileData.has_affiliate_experience === true
                          ? 'border-green-500 bg-green-50 text-green-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      } ${!isEditing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <CheckCircle2 className="mx-auto mb-1 w-5 h-5" />
                      <span className="text-sm font-medium">Yes</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => isEditing && handleInputChange("has_affiliate_experience", false)}
                      disabled={!isEditing}
                      className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                        profileData.has_affiliate_experience === false
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                      } ${!isEditing ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <Info className="mx-auto mb-1 w-5 h-5" />
                      <span className="text-sm font-medium">No</span>
                    </button>
                  </div>
                </div>

                {profileData.has_affiliate_experience === true && (
                  <div>
                    <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                      <Shield className="w-4 h-4 text-gray-400" />
                      Previous Platforms
                    </label>
                    <input
                      type="text"
                      value={profileData.previous_affiliate_platforms}
                      onChange={(e) => handleInputChange("previous_affiliate_platforms", e.target.value)}
                      disabled={!isEditing}
                      placeholder="e.g., Amazon Associates, ShareASale"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all"
                    />
                  </div>
                )}

                <div>
                  <label className="flex gap-2 items-center mb-2 text-sm font-medium text-gray-700">
                    <MessageCircle className="w-4 h-4 text-gray-400" />
                    Why Join Gwo Sevo?
                  </label>
                  <textarea
                    value={profileData.motivation_to_join}
                    onChange={(e) => handleInputChange("motivation_to_join", e.target.value)}
                    disabled={!isEditing}
                    rows={4}
                    placeholder={isLegacyUser && !profileData.motivation_to_join ? "Not set - Please share your motivation" : "Share your motivation"}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF7F7F] focus:border-transparent disabled:bg-gray-50 disabled:text-gray-500 transition-all resize-none"
                  />
                </div>
                  </div>
                )}

                {/* Tab Navigation Footer */}
                <div className="flex justify-between items-center pt-6 mt-8 border-t border-gray-200">
                  <button
                    onClick={() => {
                      const currentIndex = tabs.findIndex(t => t.id === activeTab);
                      if (currentIndex > 0) {
                        setActiveTab(tabs[currentIndex - 1].id);
                      }
                    }}
                    disabled={tabs.findIndex(t => t.id === activeTab) === 0}
                    className="flex gap-2 items-center px-4 py-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-all hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ArrowLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <div className="text-sm text-gray-500">
                    {tabs.findIndex(t => t.id === activeTab) + 1} of {tabs.length}
                  </div>
                  <button
                    onClick={() => {
                      const currentIndex = tabs.findIndex(t => t.id === activeTab);
                      if (currentIndex < tabs.length - 1) {
                        setActiveTab(tabs[currentIndex + 1].id);
                      }
                    }}
                    disabled={tabs.findIndex(t => t.id === activeTab) === tabs.length - 1}
                    className="flex items-center gap-2 px-4 py-2 text-white bg-gradient-to-r from-[#FF7F7F] to-pink-500 rounded-lg hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                    <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
                  </button>
                </div>
              </motion.div>
            </AnimatePresence>

          </div>

        </div>

        {/* Sticky Save Bar - Appears when editing */}
        {isEditing && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed right-0 bottom-0 left-0 z-50 p-4 bg-white border-t border-gray-200 shadow-2xl"
          >
            <div className="flex justify-between items-center mx-auto max-w-8xl">
              <div>
                <p className="text-sm font-medium text-gray-900">You have unsaved changes</p>
                <p className="text-xs text-gray-500">Review your changes and click save when ready</p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCancel}
                  disabled={isLoading}
                  className="px-6 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all disabled:opacity-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={isLoading}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#FF7F7F] to-pink-500 text-white rounded-lg hover:shadow-lg transition-all disabled:opacity-50 font-medium"
                >
                  <Save className="w-4 h-4" />
                  {isLoading ? "Saving..." : "Save All Changes"}
                </button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Channel Form Drawer */}
        <AnimatePresence>
          {isDrawerOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={closeChannelDrawer}
                className="fixed inset-0 z-40 bg-black bg-opacity-50"
              />
              
              {/* Drawer */}
              <ChannelFormDrawer
                isOpen={isDrawerOpen}
                channelData={channelFormData}
                setChannelData={setChannelFormData}
                channelType={selectedChannel}
                channelLabel={channelTypes.find(c => c.id === selectedChannel)?.label}
                editingIndex={editingChannelIndex}
                onClose={closeChannelDrawer}
                onSave={saveChannel}
                errors={drawerErrors}
              />
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default Profile;
